import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Card,
  Spinner,
} from "react-bootstrap";

import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";

import { useDispatch } from "react-redux";

import {
  updateroomType,
  getroomType,
} from "../../store/slices/roomtype";

import {
  gettravelHotel,
} from "../../store/slices/travelhotel";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function EditRoomType() {
  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [hotel, setHotel] = useState(null);
  const [hotelLoading, setHotelLoading] = useState(true);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    maxOccupancy: "",
    basePrice: "",
    extraBedAllowed: false,
    extraBedPrice: "",
    maxExtraBeds: "",
    childBedPrice: "",
    meals: {
      breakfast: {
        price: "",
        isIncluded: false,
      },
      lunch: {
        price: "",
        isIncluded: false,
      },
      dinner: {
        price: "",
        isIncluded: false,
      },
    },
  });

  const MAX_PRICE = 1000000;
  const MAX_OCCUPANCY = 10;
  const MAX_EXTRA_BEDS = 10;

  /* =========================
     LOAD ROOM + HOTEL
  ========================= */

  useEffect(() => {
    if (!id) return;

    loadRoomType();
  }, [id, dispatch]);

  const loadRoomType = async () => {
    try {
      setHotelLoading(true);

      /* =========================
         FETCH ROOM
      ========================= */

      const item = {
        id,
      };

      const res = await dispatch(
        getroomType(item)
      ).unwrap();

      console.log("Room Response:", res);

      const room = res?.room;

      if (!room) {
        toast.error("Room type not found");
        return;
      }

      /* =========================
         SET ROOM FORM
      ========================= */

      setForm({
        name: room.name || "",

        maxOccupancy:
          room.maxOccupancy?.toString() || "",

        basePrice:
          room.basePrice?.toString() || "",

        extraBedAllowed:
          room.extraBedAllowed || false,

        extraBedPrice:
          room.extraBedPrice?.toString() || "",

        maxExtraBeds:
          room.maxExtraBeds?.toString() || "",

        childBedPrice:
          room.childwithBedPrice?.toString() || "",

        meals: {
          breakfast:
            room.meals?.breakfast || {
              price: "",
              isIncluded: false,
            },

          lunch:
            room.meals?.lunch || {
              price: "",
              isIncluded: false,
            },

          dinner:
            room.meals?.dinner || {
              price: "",
              isIncluded: false,
            },
        },
      });

      /* =========================
         GET HOTEL ID
      ========================= */

      const hotelId =
        typeof room.hotel === "object"
          ? room.hotel?._id
          : room.hotel;

      console.log("Hotel ID:", hotelId);

      /* =========================
         FETCH HOTEL
      ========================= */

      if (hotelId) {
        try {
          const hotelResponse =
            await dispatch(
              gettravelHotel({
                id: hotelId,
              })
            ).unwrap();

          console.log(
            "Hotel Response:",
            hotelResponse
          );

          if (hotelResponse?.hotel) {
            setHotel(
              hotelResponse.hotel
            );
          }
        } catch (hotelError) {
          console.error(
            "Failed to fetch hotel:",
            hotelError
          );

          toast.error(
            "Failed to fetch hotel details"
          );
        }
      } else {
        console.warn(
          "Hotel ID not available in room response"
        );
      }

    } catch (err) {
      console.error(err);

      toast.error(
        err?.message ||
        "Failed to load room details"
      );
    } finally {
      setHotelLoading(false);
    }
  };

  /* =========================
     NUMBER ONLY
  ========================= */

  const allowOnlyNumbers = (value) =>
    value.replace(/[^0-9]/g, "");

  /* =========================
     LIMITED NUMBER
  ========================= */

  const handleLimitedNumberChange = (
    name,
    value,
    maxLimit
  ) => {
    let numericValue =
      allowOnlyNumbers(value);

    if (Number(numericValue) > maxLimit) {
      numericValue =
        maxLimit.toString();

      setErrors((prev) => ({
        ...prev,
        [name]:
          `Maximum allowed value is ${maxLimit}`,
      }));
    } else {
      setErrors((prev) => {
        const copy = { ...prev };

        if (
          Number(numericValue) <=
          maxLimit
        ) {
          delete copy[name];
        }

        return copy;
      });
    }

    setForm((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
  };

  /* =========================
     PRICE CHANGE
  ========================= */

  const handlePriceChange = (
    name,
    value,
    meal = null
  ) => {
    let numericValue =
      allowOnlyNumbers(value);

    if (Number(numericValue) > MAX_PRICE) {
      numericValue =
        MAX_PRICE.toString();

      setErrors((prev) => ({
        ...prev,
        [name]:
          `Maximum allowed amount is ₹${MAX_PRICE}`,
      }));
    } else {
      setErrors((prev) => {
        const copy = { ...prev };

        if (
          Number(numericValue) <=
          MAX_PRICE
        ) {
          delete copy[name];
        }

        return copy;
      });
    }

    if (meal) {
      setForm((prev) => ({
        ...prev,

        meals: {
          ...prev.meals,

          [meal]: {
            ...prev.meals[meal],
            price: numericValue,
          },
        },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    }
  };

  /* =========================
     VALIDATION
  ========================= */

  const validateForm = () => {
    let newErrors = {};

    if (!form.name.trim()) {
      newErrors.name =
        "Please enter room type";
    }

    if (!form.basePrice) {
      newErrors.basePrice =
        "Please enter base price";
    } else if (
      isNaN(form.basePrice) ||
      Number(form.basePrice) <= 0
    ) {
      newErrors.basePrice =
        "Base price must be a valid amount";
    }

    if (!form.maxOccupancy) {
      newErrors.maxOccupancy =
        "Please enter max occupancy";
    } else if (
      isNaN(form.maxOccupancy) ||
      Number(form.maxOccupancy) <= 0 ||
      Number(form.maxOccupancy) >
      MAX_OCCUPANCY
    ) {
      newErrors.maxOccupancy =
        `Max occupancy must be between 1 and ${MAX_OCCUPANCY}`;
    }

    if (form.extraBedAllowed) {
      if (
        !form.extraBedPrice ||
        Number(form.extraBedPrice) < 0
      ) {
        newErrors.extraBedPrice =
          "Please enter valid extra bed price";
      }

      if (
        !form.maxExtraBeds ||
        isNaN(form.maxExtraBeds) ||
        Number(form.maxExtraBeds) <= 0 ||
        Number(form.maxExtraBeds) >
        MAX_EXTRA_BEDS
      ) {
        newErrors.maxExtraBeds =
          `Max extra beds must be between 1 and ${MAX_EXTRA_BEDS}`;
      }
    }

    [
      "breakfast",
      "lunch",
      "dinner",
    ].forEach((meal) => {
      const price =
        form.meals[meal].price;

      if (price && isNaN(price)) {
        newErrors[meal] =
          "Meal price must be numeric";
      }
    });

    setErrors(newErrors);

    return (
      Object.keys(newErrors).length === 0
    );
  };

  /* =========================
     GENERAL CHANGE
  ========================= */

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));

    if (name === "name") {
      const nameRegex =
        /^[a-zA-Z0-9][\s\S]*$/;

      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          name:
            "Please enter room type",
        }));
      } else if (
        !nameRegex.test(value)
      ) {
        setErrors((prev) => ({
          ...prev,
          name:
            "Please enter letter first",
        }));
      } else if (
        value.trim().length < 3
      ) {
        setErrors((prev) => ({
          ...prev,
          name:
            "Minimum 3 characters required",
        }));
      } else if (
        value.trim().length > 30
      ) {
        setErrors((prev) => ({
          ...prev,
          name:
            "Maximum 30 characters allowed",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          name: "",
        }));
      }
    }

    if (
      name === "extraBedAllowed" &&
      !checked
    ) {
      setErrors((prev) => {
        const copy = { ...prev };

        delete copy.extraBedPrice;
        delete copy.maxExtraBeds;

        return copy;
      });
    }
  };

  /* =========================
     MEAL CHANGE
  ========================= */

  const handleMealChange = (
    meal,
    field,
    value
  ) => {
    setForm((prev) => ({
      ...prev,

      meals: {
        ...prev.meals,

        [meal]: {
          ...prev.meals[meal],
          [field]: value,
        },
      },
    }));

    if (errors[meal]) {
      setErrors((prev) => ({
        ...prev,
        [meal]: "",
      }));
    }
  };

  /* =========================
     SUBMIT
  ========================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const payload = {
        id: id,

        name: form.name.trim(),

        maxOccupancy:
          Number(form.maxOccupancy),

        basePrice:
          Number(form.basePrice),

        extraBedAllowed:
          form.extraBedAllowed,

        extraBedPrice:
          form.extraBedAllowed
            ? Number(form.extraBedPrice)
            : 0,

        maxExtraBeds:
          form.extraBedAllowed
            ? Number(form.maxExtraBeds)
            : 0,

        childwithBedPrice:
          form.extraBedAllowed
            ? Number(form.childBedPrice)
            : 0,

        meals: {
          breakfast: {
            price: Number(
              form.meals.breakfast.price ||
              0
            ),
            isIncluded:
              form.meals.breakfast
                .isIncluded,
          },

          lunch: {
            price: Number(
              form.meals.lunch.price ||
              0
            ),
            isIncluded:
              form.meals.lunch.isIncluded,
          },

          dinner: {
            price: Number(
              form.meals.dinner.price ||
              0
            ),
            isIncluded:
              form.meals.dinner.isIncluded,
          },
        },
      };

      console.log(
        "Update Room Payload:",
        payload
      );

      await dispatch(
        updateroomType(payload)
      ).unwrap();

      toast.success(
        "Room details edited successfully!"
      );

      /*
       * Go directly back to the
       * selected hotel's room list.
       */

      if (hotel?._id) {
        navigate(
          `/admin/hotel-rooms/${hotel._id}`
        );
      } else {
        navigate(-1);
      }

    } catch (err) {
      console.error(err);

      toast.error(
        err?.message ||
        err ||
        "Failed to update rooms"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* =========================
          BREADCRUMB
      ========================= */}

      <div className="breadcrumb-bar mb-2">

        <div className="breadcrumb-left">

          <Link
            to="/admin/home"
            className="breadcrumb-link active"
          >
            Home
          </Link>

          <span className="breadcrumb-separator">
            |
          </span>

          <Link
            to="/admin/roomtype"
            className="breadcrumb-link active"
          >
            Hotels
          </Link>

          <span className="breadcrumb-separator">
            |
          </span>

          {hotel?._id ? (
            <Link
              to={`/admin/hotel-rooms/${hotel._id}`}
              className="breadcrumb-link active"
            >
              {hotel.name}
            </Link>
          ) : (
            <span className="breadcrumb-link inactive">
              Hotel
            </span>
          )}

          <span className="breadcrumb-separator">
            |
          </span>

          <span className="breadcrumb-link inactive">
            Edit Room Type
          </span>

        </div>

      </div>

      <Container fluid className="p-4">

        {/* =========================
            HOTEL INFORMATION
        ========================= */}

        <Card
          className="mb-4 border-0"
          style={{
            borderRadius: "12px",
          }}
        >

          <Card.Body>

            {hotelLoading ? (

              <div className="d-flex align-items-center gap-2">

                <Spinner
                  animation="border"
                  size="sm"
                />

                <span className="text-muted">
                  Loading hotel details...
                </span>

              </div>

            ) : hotel ? (

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">

                {/* HOTEL DETAILS */}

                <div>

                  <div className="text-muted small mb-1">
                    Editing Room Type For
                  </div>

                  <h4 className="mb-1 fw-semibold">
                    {hotel.name}
                  </h4>

                  <div className="text-muted">

                    {hotel.city}

                    {hotel.location?.name
                      ? ` • ${hotel.location.name}`
                      : ""}

                    {hotel.category
                      ? ` • ${hotel.category}`
                      : ""}

                  </div>

                  {hotel.vendor?.name && (
                    <div className="text-muted small mt-1">
                      Vendor:{" "}
                      <strong>
                        {hotel.vendor.name}
                      </strong>
                    </div>
                  )}

                </div>

                {/* HOTEL SUMMARY */}

                <div className="d-flex align-items-center gap-4">

                  <div className="text-center">

                    <div className="text-muted small">
                      Total Rooms
                    </div>

                    <h5 className="mb-0 fw-semibold">
                      {hotel.totalRoom || 0}
                    </h5>

                  </div>

                  <div className="text-center">

                    <div className="text-muted small">
                      Category
                    </div>

                    <h5 className="mb-0 fw-semibold">
                      {hotel.category || "-"}
                    </h5>

                  </div>

                </div>

                {/* BACK BUTTON */}

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() =>
                    navigate(
                      `/admin/hotel-rooms/${hotel._id}`
                    )
                  }
                >
                  ← Back to Rooms
                </button>

              </div>

            ) : (

              <div className="text-danger">
                Hotel details could not be loaded.
              </div>

            )}

          </Card.Body>

        </Card>

        {/* =========================
            EDIT ROOM FORM
        ========================= */}

        <Card className="p-4 border-0">

          <Form onSubmit={handleSubmit}>

            {/* =========================
                BASIC DETAILS
            ========================= */}

            <Row className="mb-3">

              <Col md={4}>

                <Form.Label>
                  Room Type
                </Form.Label>

                <Form.Control
                  name="name"
                  placeholder="Enter room type"
                  value={form.name}
                  onChange={handleChange}
                />

                {errors.name && (
                  <small className="text-danger">
                    {errors.name}
                  </small>
                )}

              </Col>

              <Col md={4}>

                <Form.Label>
                  Max Occupancy
                </Form.Label>

                <Form.Control
                  type="text"
                  placeholder="Enter max occupancy"
                  name="maxOccupancy"
                  value={
                    form.maxOccupancy
                  }
                  onChange={(e) =>
                    handleLimitedNumberChange(
                      "maxOccupancy",
                      e.target.value,
                      MAX_OCCUPANCY
                    )
                  }
                />

                {errors.maxOccupancy && (
                  <small className="text-danger">
                    {errors.maxOccupancy}
                  </small>
                )}

              </Col>

              <Col md={4}>

                <Form.Label>
                  Base Price
                </Form.Label>

                <Form.Control
                  type="text"
                  placeholder="Enter base price"
                  name="basePrice"
                  value={
                    form.basePrice
                  }
                  onChange={(e) =>
                    handlePriceChange(
                      "basePrice",
                      e.target.value
                    )
                  }
                />

                {errors.basePrice && (
                  <small className="text-danger">
                    {errors.basePrice}
                  </small>
                )}

              </Col>

            </Row>

            {/* =========================
                EXTRA BED
            ========================= */}

            <Row className="mb-3">

              <Col md={3}>

                <Form.Check
                  label="Extra Bed Allowed"
                  name="extraBedAllowed"
                  checked={
                    form.extraBedAllowed
                  }
                  onChange={handleChange}
                />

              </Col>

              {form.extraBedAllowed && (
                <>

                  <Col md={3}>

                    <Form.Label>
                      Extra Bed Price
                    </Form.Label>

                    <Form.Control
                      type="text"
                      placeholder="Enter extra bed price"
                      value={
                        form.extraBedPrice
                      }
                      onChange={(e) =>
                        handlePriceChange(
                          "extraBedPrice",
                          e.target.value
                        )
                      }
                    />

                    {errors.extraBedPrice && (
                      <small className="text-danger">
                        {
                          errors.extraBedPrice
                        }
                      </small>
                    )}

                  </Col>

                  <Col md={3}>

                    <Form.Label>
                      Max Extra Beds
                    </Form.Label>

                    <Form.Control
                      type="text"
                      placeholder="Enter max extra beds allowed"
                      value={
                        form.maxExtraBeds
                      }
                      onChange={(e) =>
                        handleLimitedNumberChange(
                          "maxExtraBeds",
                          e.target.value,
                          MAX_EXTRA_BEDS
                        )
                      }
                    />

                    {errors.maxExtraBeds && (
                      <small className="text-danger">
                        {
                          errors.maxExtraBeds
                        }
                      </small>
                    )}

                  </Col>

                  <Col md={3}>

                    <Form.Label>
                      Child With Bed Price
                    </Form.Label>

                    <Form.Control
                      type="text"
                      placeholder="Enter child with bed price"
                      value={
                        form.childBedPrice
                      }
                      onChange={(e) =>
                        handlePriceChange(
                          "childBedPrice",
                          e.target.value
                        )
                      }
                    />

                    {errors.childBedPrice && (
                      <small className="text-danger">
                        {
                          errors.childBedPrice
                        }
                      </small>
                    )}

                  </Col>

                </>
              )}

            </Row>

            {/* =========================
                MEALS
            ========================= */}

            <h6 className="mt-4 mb-2">
              Meals
            </h6>

            {[
              "breakfast",
              "lunch",
              "dinner",
            ].map((meal) => (

              <Row
                key={meal}
                className="mb-2 align-items-center"
              >

                <Col
                  md={3}
                  className="text-capitalize"
                >
                  {meal}
                </Col>

                <Col md={3}>

                  <Form.Control
                    type="text"
                    placeholder="Enter price"
                    value={
                      form.meals[meal].price
                    }
                    onChange={(e) =>
                      handlePriceChange(
                        meal,
                        e.target.value,
                        meal
                      )
                    }
                  />

                </Col>

                <Col md={3}>

                  <Form.Check
                    label="Included"
                    checked={
                      form.meals[meal]
                        .isIncluded
                    }
                    onChange={(e) =>
                      handleMealChange(
                        meal,
                        "isIncluded",
                        e.target.checked
                      )
                    }
                  />

                </Col>

                {errors[meal] && (
                  <small className="text-danger">
                    {errors[meal]}
                  </small>
                )}

              </Row>

            ))}

            {/* =========================
                BUTTONS
            ========================= */}

            <Row className="my-5">

              <Col className="d-flex justify-content-center gap-3">

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    if (hotel?._id) {
                      navigate(
                        `/admin/hotel-rooms/${hotel._id}`
                      );
                    } else {
                      navigate(-1);
                    }
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={
                    loading ||
                    hotelLoading ||
                    !hotel
                  }
                >

                  {loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        variant="light"
                        className="me-2"
                      />

                      Submitting...
                    </>
                  ) : (
                    "Update"
                  )}

                </button>

              </Col>

            </Row>

          </Form>

        </Card>

      </Container>
    </>
  );
}